<template>
    <div>
        <ClientOnly>
            <HeaderBanner
                v-if="shouldShowHeaderBanner"
                :toast-data="toastData"
            />
        </ClientOnly>
        
        <arceus-header @backdrop-handler="toggleBackdrop" />

        <main>
            <slot />
        </main>
        
        <arceus-footer />
        
        <div 
            class="backdrop only-device" 
            :class="{ active: isBackdropActive }" 
            @click="toggleBackdrop" 
        />
    </div>
  </template>
  
  <script setup lang="ts">
  import { computed, ref, onMounted, onBeforeUnmount } from 'vue';
  import ArceusHeader from '~/components/layout/header/index.vue';
  import ArceusFooter from '~/components/layout/footer/index.vue';
  const HeaderBanner = defineAsyncComponent(() => import('~/components/common/HeaderBanner.vue'));

  interface HeaderToastData {
    id?: string
    isActive?: boolean
    link?: string
    title?: string
  }
  
  // State
  const isBackdropActive = ref(false);
  const toastData = ref<HeaderToastData>({ isActive: false });
  const runtimeConfig = useRuntimeConfig();
  const route = useRoute();

  const shouldShowHeaderBanner = computed(() => {
    if (route.meta.hideHeaderBanner === true) {
      return false;
    }
    return Boolean(toastData.value.isActive);
  });
  
  // Methods
  const toggleBackdrop = () => {
    isBackdropActive.value = !isBackdropActive.value;
  };
  
  const updateHead = () => {
    if (import.meta.client) {
      const nuxtApp = useNuxtApp();
      const $device = nuxtApp.$device || {};
      
      const classes = [
        $device?.android && 'android-device',
        $device?.apple && 'apple-device',
        $device?.tablet && 'tablet-device',
        $device?.mobile && 'mobile',
        'theme',
      ].filter(Boolean).join(' ');
      
      document.body.className = classes;
      
      if ($device?.apple) {
        document.documentElement.classList.add('apple-device');
      }
    }
  };
  
  const getToastMessage = async () => {
    try {
      const baseURL = runtimeConfig.public.API?.serverBaseURL;
      if (!baseURL) {
        return { isActive: false };
      }

      const endpoint = `${baseURL}/homepage_consumer_toast_message`;
      const response = await $fetch<HeaderToastData>(endpoint);
      toastData.value = response ?? { isActive: false };
      return response ?? { isActive: false };
    } catch (error) {
      console.error('Error fetching toast message:', error);
      return { isActive: false };
    }
  };
  
  // Lifecycle hooks
  onMounted(() => {
    updateHead();
    // Always fetch so the banner is ready after client navigations away from
    // pages that hide it; visibility is gated by shouldShowHeaderBanner.
    setTimeout(() => {
        getToastMessage();
    }, 20000);
  });
  
  onBeforeUnmount(() => {
    // Cleanup if needed
    if (import.meta.client) {
      document.body.style.overflow = '';
    }
  });
  
  // Head management for SSR
  useHead({
    htmlAttrs: {
      class: () => {
        if (import.meta.client) {
          const nuxtApp = useNuxtApp();
          const $device = nuxtApp.$device || {};
          return $device?.apple ? 'apple-device' : '';
        }
        return '';
      },
    },
    bodyAttrs: {
      class: () => {
        if (import.meta.client) {
          const nuxtApp = useNuxtApp();
          const $device = nuxtApp.$device || {};
          return [
            $device?.android && 'android-device',
            $device?.apple && 'apple-device',
            $device?.tablet && 'tablet-device',
            $device?.mobile && 'mobile',
          ].filter(Boolean).join(' ');
        }
      },
    },
  });
  </script>
  
  <style lang="scss" scoped>
  .backdrop {
    display: none;
    position: fixed;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 999;
    
    &.active {
      display: block;
    }
  }
  </style>
  
  